import { useState, useMemo, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { supabase, type Creator } from "@/lib/supabaseClient";

const CATEGORY_LIST = [
  { id: 1, name: "Ephemera & Papers" },
  { id: 2, name: "Washi Tape" },
  { id: 3, name: "Stamps & Ink" },
  { id: 4, name: "Stickers & Die Cuts" },
  { id: 5, name: "Snail Mail Kits" },
  { id: 6, name: "Journal Kits & Bundles" },
  { id: 7, name: "Fabric & Textiles" },
  { id: 8, name: "Tools & Supplies" },
];

const TYPE_FILTERS = ["All", "Individual", "Company", "Small Business", "Collective"];
const SITE_URL = import.meta.env.VITE_SITE_URL || "https://example.com";

export default function CatalogPage() {
  const [searchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get("q") || "");
  const [activeType, setActiveType] = useState("All");
  const [activeCategory, setActiveCategory] = useState("All");
  const [sortBy, setSortBy] = useState("rating");
  const [creators, setCreators] = useState<Creator[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = "Creator Catalog — Browse Junk Journal & Snail Mail Makers | Junk & Post";
    const setMeta = (name: string, content: string, prop = false) => {
      const attr = prop ? "property" : "name";
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    setMeta("description", "Browse our searchable catalog of junk journal and snail mail creators. Filter by type, category, and specialty to find the perfect handmade ephemera, washi tape, and paper crafting supplies.");
    setMeta("og:title", "Creator Catalog — Browse Junk Journal & Snail Mail Makers | Junk & Post", true);
    setMeta("og:description", "Browse our searchable catalog of junk journal and snail mail creators. Filter by type, category, and specialty.", true);
    setMeta("og:url", `${SITE_URL}/catalog`, true);

    const existing = document.getElementById("schema-catalog");
    if (existing) existing.remove();
    const script = document.createElement("script");
    script.id = "schema-catalog";
    script.type = "application/ld+json";
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      "name": "Creator Catalog — Junk Journal & Snail Mail Makers",
      "description": "Browse our searchable catalog of junk journal and snail mail creators. Filter by type, category, and specialty.",
      "url": `${SITE_URL}/catalog`,
      "isPartOf": { "@type": "WebSite", "url": SITE_URL, "name": "Junk & Post" }
    });
    document.head.appendChild(script);

    return () => {
      document.getElementById("schema-catalog")?.remove();
    };
  }, []);

  useEffect(() => {
    const q = searchParams.get("q");
    if (q) setQuery(q);
  }, [searchParams]);

  useEffect(() => {
    const fetchCreators = async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from("creators")
        .select("*")
        .eq("approved", true);
      if (!error && data) setCreators(data as Creator[]);
      setLoading(false);
    };
    fetchCreators();
  }, []);

  const filtered = useMemo(() => {
    let result = [...creators];
    if (query) {
      const q = query.toLowerCase();
      result = result.filter(
        (c) =>
          c.business_name.toLowerCase().includes(q) ||
          c.creator_name.toLowerCase().includes(q) ||
          c.specialty.toLowerCase().includes(q) ||
          (c.categories || "").toLowerCase().includes(q) ||
          (c.location || "").toLowerCase().includes(q)
      );
    }
    if (activeType !== "All") result = result.filter((c) => c.type === activeType);
    if (activeCategory !== "All")
      result = result.filter((c) =>
        (c.categories || "").toLowerCase().includes(activeCategory.toLowerCase())
      );
    if (sortBy === "rating") result.sort((a, b) => b.rating - a.rating);
    if (sortBy === "products") result.sort((a, b) => b.product_count - a.product_count);
    if (sortBy === "name") result.sort((a, b) => a.business_name.localeCompare(b.business_name));
    return result;
  }, [query, activeType, activeCategory, sortBy, creators]);

  const tags = (creator: Creator) =>
    (creator.categories || "")
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);

  return (
    <div className="min-h-screen bg-[#faf8f4]">
      <Navbar />

      {/* Page Header */}
      <div className="pt-24 pb-10 bg-[#3a2410]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 text-[#f0c87a] text-xs font-semibold uppercase tracking-widest mb-4">
            <i className="ri-book-open-line"></i>
            Creator Directory
          </div>
          <h1
            className="text-4xl font-bold text-white mb-2"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Browse the Catalog
          </h1>
          <p className="text-[#c9a87c]">
            Discover {creators.length}+ creators making beautiful things for junk journals and snail
            mail.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-10">
        {/* Search & Filters */}
        <div className="bg-white rounded-2xl border border-[#ede5d8] p-5 mb-8">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <div className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-[#9a8a78]">
                <i className="ri-search-line"></i>
              </div>
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by name, specialty, or tag..."
                className="w-full pl-11 pr-4 py-2.5 rounded-xl border border-[#ede5d8] text-sm text-[#3a2e1e] placeholder-[#9a8a78] focus:outline-none focus:border-[#c9a87c] bg-[#faf8f4]"
              />
            </div>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-4 py-2.5 rounded-xl border border-[#ede5d8] text-sm text-[#3a2e1e] bg-[#faf8f4] focus:outline-none focus:border-[#c9a87c] cursor-pointer"
            >
              <option value="rating">Sort: Top Rated</option>
              <option value="products">Sort: Most Products</option>
              <option value="name">Sort: A–Z</option>
            </select>
          </div>

          <div className="flex flex-wrap gap-2 mt-4">
            <span className="text-xs text-[#9a8a78] self-center mr-1">Type:</span>
            {TYPE_FILTERS.map((t) => (
              <button
                key={t}
                onClick={() => setActiveType(t)}
                className={`px-3 py-1 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  activeType === t
                    ? "bg-[#8b5e3c] text-white"
                    : "bg-[#f5ede0] text-[#8b5e3c] hover:bg-[#ede0cc]"
                }`}
              >
                {t}
              </button>
            ))}
            <span className="text-xs text-[#9a8a78] self-center ml-2 mr-1">Category:</span>
            <button
              onClick={() => setActiveCategory("All")}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                activeCategory === "All"
                  ? "bg-[#8b5e3c] text-white"
                  : "bg-[#f5ede0] text-[#8b5e3c] hover:bg-[#ede0cc]"
              }`}
            >
              All
            </button>
            {CATEGORY_LIST.slice(0, 6).map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.name)}
                className={`px-3 py-1 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  activeCategory === cat.name
                    ? "bg-[#8b5e3c] text-white"
                    : "bg-[#f5ede0] text-[#8b5e3c] hover:bg-[#ede0cc]"
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>
        </div>

        {/* Results Count */}
        {!loading && (
          <p className="text-sm text-[#7a6555] mb-6">
            Showing <strong>{filtered.length}</strong> creator{filtered.length !== 1 ? "s" : ""}
            {query && (
              <>
                {" "}
                matching &ldquo;<strong>{query}</strong>&rdquo;
              </>
            )}
          </p>
        )}

        {/* Loading */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {Array.from({ length: 8 }).map((_, i) => (
              <div
                key={i}
                className="bg-white rounded-2xl border border-[#ede5d8] overflow-hidden animate-pulse"
              >
                <div className="h-44 bg-[#f0e8dc]"></div>
                <div className="p-4 space-y-2">
                  <div className="h-4 bg-[#f0e8dc] rounded w-3/4"></div>
                  <div className="h-3 bg-[#f0e8dc] rounded w-1/2"></div>
                  <div className="h-3 bg-[#f0e8dc] rounded w-full"></div>
                </div>
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 text-[#c9a87c]">
              <i className="ri-search-line text-4xl"></i>
            </div>
            <h3
              className="text-xl font-bold text-[#2c1e0f] mb-2"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              No creators found
            </h3>
            <p className="text-[#7a6555]">Try adjusting your search or filters.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {filtered.map((creator) => (
              <div
                key={creator.id}
                className="bg-white rounded-2xl border border-[#ede5d8] overflow-hidden hover:-translate-y-1 transition-transform duration-300 cursor-pointer"
              >
                <div className="h-44 overflow-hidden relative bg-[#f5ede0]">
                  {creator.image_url ? (
                    <img
                      src={creator.image_url}
                      alt={creator.business_name}
                      className="w-full h-full object-cover object-top"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-[#c9a87c]">
                      <i className="ri-image-line text-4xl"></i>
                    </div>
                  )}
                  {creator.featured && (
                    <div className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-[#8b5e3c] text-white text-xs font-semibold">
                      Featured
                    </div>
                  )}
                </div>
                <div className="p-4">
                  <div className="flex items-start justify-between mb-1">
                    <h3
                      className="font-bold text-[#2c1e0f] text-base leading-tight"
                      style={{ fontFamily: "'Playfair Display', serif" }}
                    >
                      {creator.business_name}
                    </h3>
                    {creator.rating > 0 && (
                      <div className="flex items-center gap-0.5 text-[#c9a87c] ml-2 shrink-0">
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-star-fill text-xs"></i>
                        </div>
                        <span className="text-xs font-semibold text-[#3a2e1e]">
                          {creator.rating}
                        </span>
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-[#8b7355] mb-2">{creator.specialty}</p>
                  <div className="flex flex-wrap gap-1 mb-3">
                    {tags(creator)
                      .slice(0, 2)
                      .map((tag) => (
                        <span
                          key={tag}
                          className="px-2 py-0.5 rounded-full bg-[#f5ede0] text-[#8b5e3c] text-xs"
                        >
                          {tag}
                        </span>
                      ))}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-[#9a8a78]">
                      {creator.location || "Location N/A"}
                    </span>
                    <Link
                      to="/featured"
                      className="text-xs font-semibold text-[#8b5e3c] hover:text-[#7a5234] transition-colors whitespace-nowrap"
                    >
                      View →
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}
